import React, { useState } from 'react';
import {
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  FormGroupLabelHelp,
  Label,
  List,
  ListItem,
  Popover,
  Spinner,
} from '@patternfly/react-core';
import { useTemplateTokens } from '../../../hooks/useTemplateTokens';
import type { TemplateTokenCatalog } from '../../../types/models/TemplateTokenCatalog';

// The token list, the family labels and the authoring rules all come from
// GET /admin/products/template-tokens — the backend's DescriptionRenderer::TOKEN_CATALOG, which
// service/Billing/CLAUDE.md requires be updated in the same change as any emit site that gains a
// token. Nothing about the catalog is duplicated here, so a new token reaches this help with no
// frontend release.

/** The family whose whole point is that it supplies nothing beyond the auto period tokens. */
const STANDALONE_FAMILY_KEY = 'special_standalone';

/**
 * Non-blocking fallback when the catalog can't be fetched: the help must never leave the author
 * staring at an empty popover, and must never hold up the create/edit form.
 */
const fallbackContent = (
  <Content component="p">
    Text in double braces is a placeholder replaced when the invoice line is generated — tokens like{' '}
    <code>{'{{period_start}}'}</code> and <code>{'{{period_end}}'}</code>. The token reference could not
    be loaded; see the product documentation for the full list.
  </Content>
);

function CatalogBody({ catalog }: { catalog: TemplateTokenCatalog }) {
  const familyLabel = (key: string): string =>
    catalog.families.find((family) => family.key === key)?.label ?? key;
  const standaloneFamily = catalog.families.find((family) => family.key === STANDALONE_FAMILY_KEY);

  return (
    <>
      <Content component="p" className="pf-v6-u-mb-sm">
        Text in double braces is a placeholder replaced when the invoice line is generated. Everything
        else is printed verbatim. The badges say which products supply each token — only use a token
        this product actually provides.
      </Content>
      <DescriptionList isCompact isHorizontal termWidth="14ch">
        {catalog.tokens.map((token) => (
          <DescriptionListGroup key={token.token}>
            <DescriptionListTerm>
              <code>{token.token}</code>
            </DescriptionListTerm>
            <DescriptionListDescription>
              {token.meaning}
              {token.provided_by.length > 0 && (
                <div className="pf-v6-u-mt-xs">
                  {token.provided_by.map((key) => (
                    <Label key={key} isCompact variant="outline" color="blue" className="pf-v6-u-mr-xs">
                      {familyLabel(key)}
                    </Label>
                  ))}
                </div>
              )}
              {token.notes && (
                <Content
                  component="small"
                  className="pf-v6-u-display-block pf-v6-u-mt-xs pf-v6-u-text-color-subtle"
                >
                  {token.notes}
                </Content>
              )}
            </DescriptionListDescription>
          </DescriptionListGroup>
        ))}
      </DescriptionList>
      {standaloneFamily && (
        <Content component="p" className="pf-v6-u-mt-sm">
          <strong>{standaloneFamily.label}:</strong> {standaloneFamily.description}
        </Content>
      )}
      {catalog.rules.length > 0 && (
        <>
          <Content component="p" className="pf-v6-u-mt-sm">
            <strong>Template rules</strong>
          </Content>
          <List aria-label="Invoice description template rules">
            {catalog.rules.map((rule) => (
              <ListItem key={rule}>{rule}</ListItem>
            ))}
          </List>
        </>
      )}
    </>
  );
}

/**
 * The contextual-help popover for the "Invoice description" (`product_desc`) field, documenting every
 * `{{token}}` the invoicing process substitutes. Rendered via FormGroup's `labelHelp` prop (the repo
 * idiom — see MailboxCreateModal). Shared by the create and edit product modals so the token reference
 * lives in one place.
 *
 * The catalog is fetched only once the popover is first opened (and then cached for the page load by
 * useTemplateTokens), so the form never waits on it; a failed fetch degrades to a static hint.
 */
function InvoiceDescriptionLabelHelp() {
  const [hasOpened, setHasOpened] = useState(false);
  const { catalog, loading } = useTemplateTokens(hasOpened);

  let bodyContent: React.ReactNode;
  if (catalog) {
    bodyContent = <CatalogBody catalog={catalog} />;
  } else if (loading) {
    bodyContent = (
      <Content component="p">
        <Spinner size="md" aria-label="Loading the invoice description tokens" />
      </Content>
    );
  } else {
    bodyContent = fallbackContent;
  }

  return (
    <Popover
      headerContent="Invoice description tokens"
      bodyContent={bodyContent}
      maxWidth="30rem"
      onShow={() => setHasOpened(true)}
    >
      <FormGroupLabelHelp aria-label="Invoice description tokens help" />
    </Popover>
  );
}

export default InvoiceDescriptionLabelHelp;
